import { z } from 'zod';

// ---------------------------------------------------------------------------
// Analytics Schema & Hubs
// ---------------------------------------------------------------------------

// No request schema needed for GET /schema (no params)

export const analyticsHubIdParam = z.object({
  hubId: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Variables
// ---------------------------------------------------------------------------

export const analyticsVariableIdParam = z.object({
  variableId: z.string().uuid(),
});

// Variable names produced by default message_evaluator (PR1). Source of truth for chip-bar seeding + default badge — keep in sync with DEFAULT_EVALUATOR_PARAMETERS in hub-do.ts.
export const DEFAULT_EVALUATOR_VARIABLES = [
  'goal_achieved',
  'user_sentiment',
  'conversation_summary',
] as const;

export const updateVariablePinBody = z.object({
  pin: z.union([z.boolean(), z.string()]),
});

// ---------------------------------------------------------------------------
// Analytics view (chip bar selection — shared across hub admins)
// ---------------------------------------------------------------------------

export const updateAnalyticsViewBody = z.object({
  // 64 covers UUIDs (36) and the `sys:{name}` synthetic IDs for system variables.
  selected_variable_ids: z.array(z.string().min(1).max(64)).max(200),
});

// ---------------------------------------------------------------------------
// Filter & aggregation literals (single source of truth — drives Zod schemas
// below and is re-exported for runtime use by clients like the CLI).
// ---------------------------------------------------------------------------

export const NUMERIC_FILTER_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'] as const;
export const TEXT_FILTER_OPS = ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with'] as const;
export const CATEGORICAL_FILTER_OPS = ['is', 'is_not', 'in', 'not_in'] as const;
export const VARIABLE_FILTER_OPS = [
  ...NUMERIC_FILTER_OPS,
  ...TEXT_FILTER_OPS,
  ...CATEGORICAL_FILTER_OPS,
] as const;

export const STRUCTURED_QUERY_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'not_in', 'like'] as const;
export const STRUCTURED_QUERY_AGGREGATIONS = ['avg', 'sum', 'min', 'max', 'count', 'quantile', 'stddevPop', 'corr', 'uniq'] as const;

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

const filterValueSchema = z.object({
  variable_id: z.string(),
  filter_type: z.enum(VARIABLE_FILTER_OPS),
  filter_value: z.union([z.string(), z.number()]),
});

export const conversationsBody = z.object({
  hub_id: z.string().uuid(),
  time_interval: z.object({
    start: z.string().optional(),
    end: z.string().optional(),
  }).optional(),
  filters: z.array(filterValueSchema).optional(),
  limit: z.number().int().positive().max(500).default(50),
  offset: z.number().int().min(0).default(0),
});

// ---------------------------------------------------------------------------
// Conversation Messages
// ---------------------------------------------------------------------------

export const analyticsConversationIdParam = z.object({
  conversationId: z.string().uuid(),
});

export const messagesQuery = z.object({
  hub_id: z.string().uuid(),
  limit: z.string().regex(/^\d+$/).optional(),
  offset: z.string().regex(/^\d+$/).optional(),
});

// ---------------------------------------------------------------------------
// Conversation Detail Data
// ---------------------------------------------------------------------------

export const conversationDetailDataQuery = z.object({
  hub_id: z.string().uuid(),
});

export type ConversationDetailDataQuery = z.infer<typeof conversationDetailDataQuery>;

// ---------------------------------------------------------------------------
// Analytics Data
// ---------------------------------------------------------------------------

export const analyticsDataBody = z.object({
  hub_id: z.string().uuid(),
  periodicity: z.enum(['daily', 'weekly', 'monthly', 'yearly']),
  time_interval: z.object({
    start: z.string(),
    end: z.string(),
  }),
  trend_analysis: z.boolean(),
  filters: z.array(filterValueSchema).optional(),
  selected_variables: z.array(z.string()),
  include_summary: z.boolean().optional(),
  include_variables: z.boolean().optional(),
  variable_origin: z.enum(['agent', 'system', 'annotation']).optional(),
  variable_category: z.string().optional(),
  variables_only: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Structured Query
// ---------------------------------------------------------------------------

export const structuredQueryBody = z.object({
  hub_id: z.string().uuid(),
  metrics: z.array(z.object({
    column: z.string(),
    aggregation: z.enum(STRUCTURED_QUERY_AGGREGATIONS),
    alias: z.string().optional(),
    corr_with: z.string().optional(),
    quantile_level: z.number().min(0).max(1).optional(),
  })).min(1),
  group_by: z.array(z.string()).optional(),
  order_by: z.array(z.object({
    column: z.string(),
    direction: z.enum(['asc', 'desc']).optional(),
  })).optional(),
  filters: z.array(z.object({
    column: z.string(),
    operator: z.enum(STRUCTURED_QUERY_OPS),
    value: z.union([z.string(), z.number(), z.boolean()]),
  })).optional(),
  time_range: z.object({
    start: z.string(),
    end: z.string(),
  }).optional(),
  limit: z.number().int().positive().max(10000).optional(),
});

// ---------------------------------------------------------------------------
// Read-only SQL (CLI agent surface — POST /sql, GET /sql/schema)
// ---------------------------------------------------------------------------

export const analyticsSqlBody = z.object({
  hub_id: z.string().uuid(),
  // Hard size cap before the validator runs — keeps lex/parse work bounded.
  sql: z.string().min(1).max(8000),
  limit: z.number().int().positive().max(10000).optional(),
});

export const analyticsSqlSchemaQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type AnalyticsHubIdParam = z.infer<typeof analyticsHubIdParam>;
export type AnalyticsVariableIdParam = z.infer<typeof analyticsVariableIdParam>;
export type UpdateVariablePinBody = z.infer<typeof updateVariablePinBody>;
export type UpdateAnalyticsViewBody = z.infer<typeof updateAnalyticsViewBody>;
export type ConversationsBody = z.infer<typeof conversationsBody>;
export type AnalyticsConversationIdParam = z.infer<typeof analyticsConversationIdParam>;
export type MessagesQuery = z.infer<typeof messagesQuery>;
export type AnalyticsDataBody = z.infer<typeof analyticsDataBody>;
export type StructuredQueryBody = z.infer<typeof structuredQueryBody>;
export type AnalyticsSqlBody = z.infer<typeof analyticsSqlBody>;
export type AnalyticsSqlSchemaQuery = z.infer<typeof analyticsSqlSchemaQuery>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

// ---------------------------------------------------------------------------
// Shared primitives (used across multiple responses)
// ---------------------------------------------------------------------------

export type NumericFilterType = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte';
export type TextFilterType = 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with';
export type CategoricalFilterType = 'is' | 'is_not' | 'in' | 'not_in';
export type FilterType = NumericFilterType | TextFilterType | CategoricalFilterType;

// 'annotation' = post-hoc, human/CLI-set business outcomes (data.annotations.*),
// distinct from agent-inferred 'agent' variables and engine 'system' metrics.
export type VariableOrigin = 'agent' | 'system' | 'annotation';
export type Periodicity = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface TimeInterval {
  start: string;
  end: string;
}

export interface VariableFilter {
  variable_id: string;
  filter_type: FilterType;
  filter_value: string | number;
}

// ---------------------------------------------------------------------------
// GET /schema — analytics hierarchy
// ---------------------------------------------------------------------------

export interface AnalyticsSchemaResponse {
  data: {
    flat: {
      organizations: Array<{ id: string; name: string }>;
      hubsByOrg: Record<string, Array<{ id: string; name: string }>>;
    };
    lastUpdated: string;
  };
}

// ---------------------------------------------------------------------------
// GET /hubs — hubs list
// ---------------------------------------------------------------------------

export interface AnalyticsHubInfo {
  hub_id: string;
  hub_name: string;
  hub_description: string | null;
}

export interface AnalyticsHubsListResponse {
  success: true;
  hubs: AnalyticsHubInfo[];
}

// ---------------------------------------------------------------------------
// GET /hubs/:hubId/variables — variables by hub
// ---------------------------------------------------------------------------

export interface AnalyticsCategoryInfo {
  category: string;
  label: string;
  variableCount: number;
}

export interface AnalyticsConversationVariable {
  variable_id: string;
  variable_name: string;
  variable_description?: string;
  variable_label?: string;
  variable_type: 'numeric' | 'categorical' | 'text';
  variable_origin: string;
  variable_category: string;
  variable_enum: unknown[];
  pin: boolean;
  hub_id?: string;
}

export interface AnalyticsVariablesByHubResponse {
  success: true;
  data: {
    categories: AnalyticsCategoryInfo[];
    variablesByCategory: Record<string, AnalyticsConversationVariable[]>;
  };
}

// ---------------------------------------------------------------------------
// PATCH /variables/:variableId/pin — update pin status
// ---------------------------------------------------------------------------

export interface AnalyticsUpdatePinResponse {
  success: true;
  data: {
    variable_id: string;
    pin: boolean;
  };
}

// ---------------------------------------------------------------------------
// GET / PUT /hubs/:hubId/selection — analytics view (chip bar) selection
// ---------------------------------------------------------------------------

export interface AnalyticsViewResponse {
  success: true;
  data: {
    selected_variable_ids: string[];
    seeded: boolean;
  };
}

// ---------------------------------------------------------------------------
// POST /conversations — conversation list
// ---------------------------------------------------------------------------

export interface AnalyticsConversationListItem {
  conversation_id: string;
  created_at?: string;
  updated_at?: string;
  message_count?: number;
  message_text?: string;
  participant_name?: string;
  participant_phone?: string;
  hub_name?: string;
  user_name?: string;
  user_sentiment?: string;
  goal_achieved?: string;
  [key: string]: unknown;
}

export interface AnalyticsConversationsListResponse {
  success: true;
  conversations: AnalyticsConversationListItem[];
  total: number;
  offset: number;
  limit: number;
  /**
   * True when ClickHouse was cold and the response is a provisional empty
   * result — the client should keep polling until this flag disappears.
   */
  cold?: boolean;
}

// ---------------------------------------------------------------------------
// GET /conversations/:conversationId/messages — messages
// ---------------------------------------------------------------------------

export interface AnalyticsConversationMessage {
  message_id: string;
  message_key?: string;
  created_at: string;
  message_text?: string;
  message_type: string;
  sender_type: string;
  receiver_type: string;
  channel_type: string;
  sender_name?: string;
}

export interface AnalyticsConversationMessagesResponse {
  success: true;
  messages?: AnalyticsConversationMessage[];
  conversation?: {
    conversation_id: string;
    participant_name?: string;
  };
}

// ---------------------------------------------------------------------------
// GET /conversations/:conversationId/data — conversation detail data
// ---------------------------------------------------------------------------

export interface ConversationDetailVariableValue {
  name: string;
  value: string | number | null;
  label?: string;
  description?: string;
  type?: 'numeric' | 'categorical' | 'text';
  category?: string;
  variable_enum?: unknown[];
}

export interface ConversationDetailDataResponse {
  success: true;
  conversation_id: string;
  hub_id: string;
  system: ConversationDetailVariableValue[];
  variables: ConversationDetailVariableValue[];
  meta: Record<string, string | number | null>;
  /**
   * True when ClickHouse was cold and the response is a provisional empty
   * result — the client should keep polling until this flag disappears.
   */
  cold?: boolean;
}

// ---------------------------------------------------------------------------
// POST /data — analytics data (numeric, categorical, text)
// ---------------------------------------------------------------------------

export interface AnalyticsTrendDataPoint {
  period: string;
  value: number | string | Record<string, number>;
}

export interface AnalyticsMetrics {
  average: number;
  sum: number;
  minimum: number;
  maximum: number;
  count?: number;
  median?: number;
}

export interface AnalyticsDataItem {
  variable: string;
  variable_id: string;
  type: 'numeric' | 'categorical' | 'text';
  data?: AnalyticsTrendDataPoint[];
  metrics?: AnalyticsMetrics;
  distribution?: Record<string, number>;
}

export interface AnalyticsSummaryTrendData {
  period: string;
  total_conversations: number;
  ai_only_rate: number;
  avg_response_time: number;
  assistant_response_time: number;
  support_response_time: number;
  user_response_time: number;
}

export interface AnalyticsSummaryData {
  total_conversations: number;
  ai_only_rate: number;
  avg_response_time: number;
  assistant_response_time: number;
  support_response_time: number;
  user_response_time: number;
  data?: AnalyticsSummaryTrendData[];
}

/** Response for variables_only=true requests */
export interface AnalyticsVariablesOnlyResponse {
  variables: unknown[];
}

/** Full analytics data response (POST /data) */
export interface AnalyticsDataResponse {
  analytics: AnalyticsDataItem[];
  summary?: AnalyticsSummaryData;
  variables?: unknown[];
  /**
   * True when any of the parallel fan-out queries (numeric / categorical /
   * text / summary) returned a cold-start outcome. The frontend uses this
   * single flag to flip the cold store and show the cold-start banner.
   */
  cold?: boolean;
}

// ---------------------------------------------------------------------------
// POST /query — structured analytics query
// ---------------------------------------------------------------------------

export interface AnalyticsStructuredQueryResponse {
  success: true;
  data: Record<string, unknown>[];
  statistics?: {
    elapsed: number;
    rows_read: number;
    bytes_read: number;
  };
}

// ---------------------------------------------------------------------------
// POST /sql — read-only SQL (CLI agent surface). Same shape as /query plus a
// `cold` flag so the agent can distinguish a genuine empty result from a
// ClickHouse cold-start (mirrors the existing analytics cold-start contract
// on `/conversations`, `/data`, `/conversations/:id/data`).
// ---------------------------------------------------------------------------

export interface AnalyticsSqlResponse {
  success: true;
  data: Record<string, unknown>[];
  statistics?: {
    elapsed: number;
    rows_read: number;
    bytes_read: number;
  };
  cold?: boolean;
}

// ---------------------------------------------------------------------------
// GET /sql/schema — column + variable-path catalog the agent can introspect
// ---------------------------------------------------------------------------

export interface AnalyticsSqlSchemaPath {
  path: string;
  type: 'numeric' | 'categorical' | 'text' | 'unknown';
  origin: 'system' | 'agent' | 'annotation';
  category?: string;
  description?: string;
}

export interface AnalyticsSqlSchemaResponse {
  success: true;
  data: {
    table: 'conversation';
    top_level: Array<{ column: string; type: string }>;
    paths: AnalyticsSqlSchemaPath[];
  };
}
